/**
 * Footer — 3 variants (minimal, multi_col, big_brand)
 */
import type { CSSProperties, ReactNode } from 'react';

import { Body, SectionFrame } from './primitives';
import type { FooterContent } from '../schemas/footer';
import type { BrandContext } from '../types/index';

interface FT {
  content: FooterContent;
  brand?: BrandContext;
}

const SOCIAL_PLATFORMS: Array<{ key: string; label: string }> = [
  { key: 'twitter', label: 'Twitter' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'linkedin', label: 'LinkedIn' },
];

/** ブランド設定の SNS リンク配列を取得（URL が空の項目は除外） */
function getActiveSocialLinks(
  brand: BrandContext | undefined,
): Array<{ key: string; label: string; href: string }> {
  const links = brand?.socialLinks;
  if (!links) return [];
  return SOCIAL_PLATFORMS.flatMap((p) => {
    const href = links[p.key];
    return href ? [{ ...p, href }] : [];
  });
}

function SocialLinkRow({
  brand,
  style,
  size = 'sm',
}: {
  brand: BrandContext | undefined;
  style?: CSSProperties;
  size?: 'sm' | 'md';
}): ReactNode {
  const items = getActiveSocialLinks(brand);
  if (items.length === 0) return null;
  const fontSize = size === 'md' ? '0.95rem' : '0.85rem';
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', ...style }}>
      {items.map((it) => (
        <a
          key={it.key}
          href={it.href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'var(--ls-text-muted)',
            textDecoration: 'none',
            fontSize,
          }}
        >
          {it.label}
        </a>
      ))}
    </div>
  );
}

export function FooterMinimal({ content, brand }: FT) {
  return (
    <SectionFrame background="surface">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <div style={{ fontFamily: 'var(--ls-font-heading)', fontSize: '1.25rem', fontWeight: 600 }}>
          {content.brand}
        </div>
        {content.tagline && (
          <Body style={{ fontSize: '0.875rem', maxWidth: 480 }}>{content.tagline}</Body>
        )}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
          {asArray(content.columns).flatMap((col) =>
            asArray(col.links).map((l, i) => (
              <a
                key={`${col.title}-${i}`}
                href={l.href ?? '#'}
                style={{
                  color: 'var(--ls-text-muted)',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                }}
              >
                {l.label}
              </a>
            )),
          )}
        </div>
        <SocialLinkRow brand={brand} style={{ justifyContent: 'center' }} />
        {content.copyright && (
          <Body style={{ fontSize: '0.75rem', opacity: 0.6 }}>{content.copyright}</Body>
        )}
      </div>
    </SectionFrame>
  );
}

export function FooterMultiCol({ content, brand }: FT) {
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div className="ls-footer-cols">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div
              style={{ fontFamily: 'var(--ls-font-heading)', fontSize: '1.25rem', fontWeight: 600 }}
            >
              {content.brand}
            </div>
            {content.tagline && <Body style={{ fontSize: '0.85rem' }}>{content.tagline}</Body>}
            <SocialLinkRow brand={brand} />
          </div>
          {asArray(content.columns)
            .slice(0, 4)
            .map((col, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--ls-text-muted)',
                  }}
                >
                  {col.title}
                </div>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {asArray(col.links).map((l, j) => (
                    <li key={j}>
                      <a
                        href={l.href ?? '#'}
                        style={{
                          color: 'var(--ls-text)',
                          textDecoration: 'none',
                          fontSize: '0.9rem',
                        }}
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
        {content.copyright && (
          <div
            style={{
              borderTop: '1px solid var(--ls-border)',
              paddingTop: 16,
              fontSize: '0.8rem',
              color: 'var(--ls-text-muted)',
            }}
          >
            {content.copyright}
          </div>
        )}
      </div>
    </SectionFrame>
  );
}

export function FooterBigBrand({ content, brand }: FT) {
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 64 }}>
        <h2
          style={{
            fontFamily: 'var(--ls-font-heading)',
            fontSize: 'clamp(3rem, 12vw, 14rem)',
            fontWeight: 700,
            lineHeight: 0.9,
            letterSpacing: '-0.04em',
            margin: 0,
            color: 'var(--ls-text)',
          }}
        >
          {content.brand}
        </h2>
        <div className="ls-grid ls-grid-3" style={{ gap: 32 }}>
          {asArray(content.columns)
            .slice(0, 3)
            .map((col, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--ls-text-muted)',
                  }}
                >
                  {col.title}
                </div>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {asArray(col.links).map((l, j) => (
                    <li key={j}>
                      <a
                        href={l.href ?? '#'}
                        style={{
                          color: 'var(--ls-text)',
                          textDecoration: 'none',
                          fontSize: '0.95rem',
                        }}
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
        <SocialLinkRow brand={brand} size="md" />
        {content.copyright && (
          <div
            style={{
              borderTop: '1px solid var(--ls-border)',
              paddingTop: 16,
              fontSize: '0.8rem',
              color: 'var(--ls-text-muted)',
            }}
          >
            {content.copyright}
          </div>
        )}
      </div>
    </SectionFrame>
  );
}

export const footerVariants = {
  minimal: FooterMinimal,
  multi_col: FooterMultiCol,
  big_brand: FooterBigBrand,
};

/**
 * 保存済みデータが配列でない場合（編集画面で列を追加した際に links が空文字で
 * 保存されていた不具合: 2026-09-25 修正）でもページ全体が落ちないよう、配列以外は空として扱う。
 */
function asArray<T>(v: T[] | null | undefined): T[] {
  return Array.isArray(v) ? v : [];
}
